import * as React from 'react';
import { useDynamicData, loadScript, useCurrentLanguage } from '@avensia/excite';
import { ARIcon, FullscreenIcon, DimensionsIcon } from 'webapp/components/icons';
import { styled } from '@glitz/react';
import { applyImageBackground } from './helpers';

declare global {
  namespace React.JSX {
    interface IntrinsicElements {
      'charpstar-container': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement> & {
          'data-articleid'?: string;
          'data-language'?: string;
        },
        HTMLElement
      >;
    }
  }
}

const CHARPSTAR_ALLOWED_LOCALES = [
  'en-gb',
  'en-us',
  'sv-se',
  'no-no',
  'da-dk',
  'de-at',
  'de-de',
  'lv-lv',
  'cs-cz',
  'pl-pl',
  'sk-sk',
  'et-ee',
  'fi-fi',
  'lt-lt',
  'es',
];
const CHARPSTAR_DEFAULT_LOCALE = 'en-us';
const CHARPSTAR_SOURCE_NAME = 'Charpstar';

export default function useProduct3DModel(sku: string, source3dModel: string | undefined) {
  const [product3dModelAvailable, setProduct3dModelAvailable] = React.useState(false);
  const [scriptUrl] = useDynamicData<NordicNest.Api.ScriptSettingsApiModel, string | undefined>({
    selector: data => data?.charpstarWidgetScriptUrl,
    shouldResolveData: !!source3dModel,
  });
  const [initialized, setInitialized] = React.useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [product3dModelThumbnail] = useDynamicData<
    NordicNest.Api.ScriptSettingsApiModel,
    Excite.ContentApi.ImageApiModel | Excite.ContentApi.VideoApiModel | undefined
  >({
    selector: data => data?.product3dModelThumbnail,
    shouldResolveData: product3dModelAvailable,
  });

  const fullscreenEventListener = React.useCallback(() => {
    if (!document.fullscreenElement) {
      setIsFullscreen(false);
      return;
    }

    setIsFullscreen(true);
  }, []);

  React.useEffect(() => {
    addEventListener('fullscreenchange', fullscreenEventListener);

    return () => {
      removeEventListener('fullscreenchange', fullscreenEventListener);
    };
  }, [fullscreenEventListener]);

  React.useEffect(() => {
    if (source3dModel === CHARPSTAR_SOURCE_NAME) {
      if (scriptUrl) {
        // We do not want to set the 3D model as available until we know we have the charpstar source AND the script
        // URL to prevent flickering. But we want to set it early to mount the charpstar component before the script runs.
        setProduct3dModelAvailable(true);

        loadScript(scriptUrl, {
          async: false,
          callback: element => {
            element.defer = true;
            return element;
          },
        }).then(async () => {
          if (!initialized && sku) {
            const availability = (await window.charpstAR?.checkProductStatus(sku)) ?? false;
            setProduct3dModelAvailable(availability);

            if (availability) {
              window.charpstAR?.init();
              setInitialized(true);
            }
          }
        });

        return () => {
          if (initialized) {
            window.charpstAR?.destroy();
          }
        };
      }
    }
  }, [scriptUrl, source3dModel, sku, initialized]);

  React.useEffect(() => {
    if (!window.charpstAR?.isReady()) return;

    if (isFullscreen) window.charpstAR?.enableScrollToZoom();
    if (!isFullscreen) window.charpstAR?.disableScrollToZoom();
  }, [isFullscreen]);

  const toggleFullscreen = React.useCallback(() => {
    if (ref.current) {
      if (document.fullscreenElement) {
        document.exitFullscreen();
        return;
      }

      if (ref.current.requestFullscreen) {
        ref.current.requestFullscreen();
      }
    }
  }, []);

  const product3dModelComponent = product3dModelAvailable ? (
    <Product3dModel
      sku={sku}
      initialized={initialized}
      ref={ref}
      toggleFullscreen={toggleFullscreen}
      isFullscreen={isFullscreen}
    />
  ) : undefined;

  return {
    product3dModelAvailable,
    product3dModelThumbnail,
    product3dModelComponent,
  };
}

type PropType = {
  sku: string;
  initialized: boolean;
  toggleFullscreen: () => void;
  isFullscreen: boolean;
};

const Product3dModel = React.forwardRef<HTMLDivElement, PropType>(
  ({ sku, initialized, toggleFullscreen, isFullscreen }: PropType, ref) => {
    const currentLanguage = useCurrentLanguage();
    const [dimensionsActive, setDimensionsActive] = React.useState(true);

    React.useEffect(() => {
      if (!initialized) return;

      const waitForCharpstar: () => Promise<boolean> = async () => {
        let i = 0;
        let isReady = !!window.charpstAR?.isReady();
        while (!isReady) {
          await new Promise(resolve => setTimeout(resolve, 100));
          isReady = !!window.charpstAR?.isReady();

          if (isReady) return true;
          if (i > 50) return false; // Timeout after 5 seconds
          i++;
        }

        return true;
      };

      waitForCharpstar().then(result => {
        if (result) {
          window.charpstAR?.disableScrollToZoom();
          window.charpstAR?.enableDimensions();
        }
      });
    }, [initialized]);

    const charpstarLocale = CHARPSTAR_ALLOWED_LOCALES.includes(currentLanguage.toLowerCase())
      ? currentLanguage.toLowerCase()
      : CHARPSTAR_DEFAULT_LOCALE;

    const toggleDimensions = () => {
      if (!window.charpstAR?.isReady()) return;

      if (dimensionsActive) window.charpstAR?.disableDimensions();
      if (!dimensionsActive) window.charpstAR?.enableDimensions();

      setDimensionsActive(prev => !prev);
    };

    return (
      <Base ref={ref} style={isFullscreen ? { backgroundColor: 'white' } : undefined}>
        <charpstar-container
          data-articleid={sku}
          data-language={charpstarLocale}
          style={{ display: 'none' }}
        ></charpstar-container>
        <ButtonContainer>
          <DimensionsIcon style={{ width: '24px', height: 'auto' }} onClick={toggleDimensions} />
          <FullscreenIcon style={{ width: '24px', height: 'auto' }} onClick={toggleFullscreen} />
          {!isFullscreen && (
            <ARIcon style={{ width: '24px', height: 'auto' }} onClick={() => window.charpstAR?.activateAR()} />
          )}
        </ButtonContainer>
      </Base>
    );
  },
);

const Base = styled.div(
  {
    width: '100%',
    height: '100%',
  },
  applyImageBackground
    ? {
        backgroundColor: t => t.imageBackgroundColor,
      }
    : {
        backgroundColor: 'white',
      },
);

const ButtonContainer = styled.div({
  position: 'absolute',
  bottom: '25px',
  right: '25px',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px',
});
